import { IntervalUnit } from "@gocardless/api/dashboard/types";

import { Currency } from "src/common/currencies";
import { TemplateProps } from "src/components/global-state/SetupPaymentsProvider";
import {
  PaymentType,
  Timeframe,
} from "src/components/routes/SetupPayments/common/type";
import { SubSegment } from "src/components/routes/SetupPayments/common/consts/subsegment";

export interface TemplateItem {
  key: string;
  useCase: string;
  title: string;
  description: string;
  question?: string;
  answers?: {
    yes: {
      templateProps?: TemplateProps;
      paymentNamePlaceholder?: string;
    };
    no: {
      templateProps?: TemplateProps;
      paymentNamePlaceholder?: string;
    };
  };
  templateProps?: TemplateProps;
}

export const DEFAULT_TEMPLATE_PROPS = {
  paymentType: PaymentType.Mandate,
  paymentNamePlaceholder: "",
  formValues: {},
};

export const BASE_TEMPLATES: Partial<
  Record<SubSegment, readonly TemplateItem[]>
> = {
  [SubSegment.MembershipClubsNonSports]: [
    {
      key: "membership_clubs_non_sports_plan_monthly",
      useCase: "Collect membership fees monthly",
      title: "Monthly membership fees",
      description:
        "e.g. £20 per month for a fixed term or until further notice",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Event Fee",
        introTitle: "Charge for a one-off product or service",
        introDescription: "Request a payment from one or multiple customers",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "membership_clubs_non_sports_plan_yearly",
      useCase: "Collect membership fees annually",
      title: "Annual membership fees",
      description: "e.g. £50 per year for annual club membership",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Annual Club Membership",
        introTitle: "Collect member fees",
        introDescription:
          "Use the subscription option to create membership plans",
        formValues: {
          interval_unit: IntervalUnit.Yearly,
        },
      },
    },
    {
      key: "membership_clubs_non_sports_oneoff",
      useCase: "Charge for one-off fees e.g. events or donations",
      title: "One-off fees",
      description: "e.g. an instant payment of £200 for a club event",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Event Fee",
        introTitle: "Charge for a one-off product or service",
        introDescription: "Request a payment from one or multiple customers",
        formValues: {},
      },
    },
  ],
  [SubSegment.SportsClubs]: [
    {
      key: "sports_clubs_plan_monthly",
      useCase: "Collect membership or subscription fees monthly",
      title: "Monthly membership fees",
      description: "e.g. £20 per month for club membership",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Training Fees",
        introTitle: "Collect monthly fees",
        introDescription:
          "Use the subscription option to create a monthly plan and invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "sports_clubs_plan_yearly",
      useCase: "Collect membership fees annually",
      title: "Annual membership fees",
      description: "e.g. £150 per year for club membership",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Annual Family Membership",
        introTitle: "Collect annual membership fees",
        introDescription:
          "Use the subscription option to create a yearly plan and invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Yearly,
        },
      },
    },
    {
      key: "sports_clubs_plan_monthly_count_3",
      useCase:
        "Collect recurring fees for a set period, e.g., a term of lessons",
      title: "Recurring fees for a set period",
      description: "e.g. 3 monthly payments of £30 for a term of lessons",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Summer Term Fees Paid Monthly",
        introTitle: "Collect recurring fees for a set period",
        introDescription:
          "Use the subscription option for a set period e.g. monthly for 3 payments",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          timeframe: Timeframe.NumberOfPayment,
          count: 3,
        },
      },
    },
    {
      key: "sports_clubs_oneoff",
      useCase: "Charge for one-off fees",
      title: "One-off fee",
      description: "e.g. an instant payment of £50 for club merchandise",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Tournament Fee",
        introTitle: "Charge for a one-off product or service",
        introDescription: "Request a payment from one or multiple customers",
        formValues: {},
      },
    },
  ],
  [SubSegment.FoodAndDrink]: [
    {
      key: "food_and_drink_plan_monthly",
      useCase: "Offer subscription plans",
      title: "Subscription plan",
      description: "e.g. £100 per month for Gold Coffee Club Membership",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Gold Membership",
        introTitle: "Offer subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "food_and_drink_oneoff",
      useCase: "Charge for one-off orders or services",
      title: "One-off order or service",
      description: "e.g. £900 for an invoice",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Payment for Invoice 1500",
        introTitle: "Charge for a one-off product or service",
        introDescription:
          "Share a payment link or include it on your website and invoices to receive payments",
        formValues: {},
      },
    },
    {
      key: "food_and_drink_mandate",
      useCase:
        "Auto charge regular customers based on usage or order quantities",
      title: "Auto charge regular customers based on usage",
      description:
        "Collect variable amounts whenever due based on order quantities or usage",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    },
    {
      key: "food_and_drink_instalment",
      useCase: "Split big payments into instalments",
      title: "Split a big payment into instalments",
      description: "e.g. £900 for an order paid in 3 equal monthly instalments",
      templateProps: {
        paymentType: PaymentType.Instalment,
        paymentNamePlaceholder: "e.g. Catering Services",
        introTitle: "Split a large payment into instalments",
        introDescription:
          "Set up the details below, then invite customers to the plan",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          numberOfInstalments: 3,
        },
      },
    },
  ],
  [SubSegment.CleaningServices]: [
    {
      key: "cleaning_services_plan_monthly",
      useCase: "Offer subscription plans for cleaning services",
      title: "Subscription for cleaning services",
      description: "e.g. £200 per month for ongoing services",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Monthly Clean",
        introTitle: "Offer subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "cleaning_services_mandate",
      useCase: "Auto charge regular customers after each clean",
      title: "Auto charge regular customers after each clean",
      description: "Collect ad-hoc payments whenever due",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    },
    {
      key: "cleaning_services_oneoff",
      useCase: "Charge for a one-off service",
      title: "One-off service",
      description: "e.g. an instant payment of £500 for a one-time clean",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Cleaning Service",
        introTitle: "Charge for a one-off service",
        introDescription: "Request a payment from someone who owes you",
        formValues: {},
      },
    },
  ],
  [SubSegment.ConsultingServices]: [
    {
      key: "consulting_services_plan_monthly_50",
      useCase: "Offer subscription plans",
      title: "Subscription plans",
      description: "e.g. £50 per month for Gold Plan",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Gold Plan",
        introTitle: "Offer subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "consulting_services_plan_monthly_200",
      useCase: "Collect recurring retainer fees",
      title: "Retainer fees",
      description: "e.g. £200 per month for consultancy services",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Retainer for Services",
        introTitle: "Collect retainer fees",
        introDescription:
          "Use the subscription option to set up customers on plans for ongoing services or for a set number of payments.",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "consulting_services_mandate",
      useCase:
        "Auto charge regular clients based on usage, services or time-worked",
      title: "Auto charge regular clients based on usage",
      description:
        "Collect variable payments based on services delivered or time-worked",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    },
    {
      key: "consulting_services_oneoff",
      useCase: "Charge for one-off services or products",
      title: "One-off service or product",
      description: "e.g. an instant payment of £2000 for a one-off project",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Consultancy Fees",
        introTitle: "Charge for a one-off service",
        introDescription:
          "Share a payment link or include it in invoices to receive payments",
        formValues: {},
      },
    },
  ],
  [SubSegment.RetailAndWholesale]: [
    {
      key: "retail_and_wholesale_plan_a",
      useCase: "Offer subscription plans",
      title: "Subscription plan",
      description: "e.g. £50 per month for Gold Plan",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Premium Subscription",
        introTitle: "Offer subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "retail_and_wholesale_plan_b",
      useCase: "Collect rental or hire payments",
      title: "Rental or hire payments",
      description: "e.g. £200 per month for storage rental",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Rental Fees",
        introTitle: "Collect rental or hire payments",
        introDescription:
          "Use the subscription option to set up customers on plans for ongoing rental or for a set number of payments.",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "retail_and_wholesale_oneoff",
      useCase: "Charge for one-off goods or services",
      title: "One-off goods or services",
      description: "e.g. an instant payment of £2000 for an invoice",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Payment for Invoice 1500",
        introTitle: "Charge for a one-off goods or services",
        introDescription:
          "Share a payment link or include it in invoices to receive payments",
        formValues: {},
      },
    },
    {
      key: "retail_and_wholesale_instalment",
      useCase: "Split a big payment into instalments",
      title: "Split a big payment into instalments",
      description: "e.g. £300 split into 3 monthly payments of £100 ",
      templateProps: {
        paymentType: PaymentType.Instalment,
        paymentNamePlaceholder: "e.g. Instalment Plan for Equipment",
        introTitle: "Split a large payment into instalments",
        introDescription:
          "Set up the details below, then invite customers to the plan",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          numberOfInstalments: 3,
        },
      },
    },
  ],
  [SubSegment.CharityAndDonation]: [
    {
      key: "charity_and_donation_plan",
      useCase: "Offer subscription, membership, or donation plans",
      title: "Subscription, membership or donation plans",
      description: "e.g. £20 per month for a regular donation",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Regular Donation",
        introTitle: "Offer subscription, membership or donation plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "charity_and_donation_oneoff",
      useCase: "Charge for one-off payments, e.g., donations or events",
      title: "One-off payment",
      description: "e.g. an instant payment of £20 for a donation",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Event Fee",
        introTitle: "Charge for a one-off payment",
        introDescription: "Request a payment from one or multiple customers",
        formValues: {},
      },
    },
    {
      key: "charity_and_donation_plan_count_8",
      useCase: "Offer fixed-term payment plans, e.g., a term of classes",
      title: "Fixed term payment plan",
      description: "e.g. £50 a month for 8 payments",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Term Fees",
        introTitle: "Offer fixed-term payment plans ",
        introDescription:
          "Use the subscription option to collect recurring fees for a set number of payments",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          timeframe: Timeframe.NumberOfPayment,
          count: 3,
        },
      },
    },
  ],
  [SubSegment.HomeServices]: [
    {
      key: "home_services_plan_a",
      useCase: "Offer service, maintenance or subscription plans",
      title: "Service, maintenance or subscription plans",
      description: "e.g. £20 per month for ongoing services",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Gold Service Plan",
        introTitle: "Offer service. maintenance or subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "home_services_plan_b",
      useCase: "Collect monthly rental payments",
      title: "Monthly rental payments",
      description: "e.g. £50 per month for rent",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Monthly Rent",
        introTitle: "Collect monthly rental payments",
        introDescription:
          "Use the subscription option to set up customers on plans for ongoing rental or for a set number of payments.",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "home_services_oneoff",
      useCase: "Charge for one-off payments",
      title: "One-off payment",
      description: "e.g. an instant payment of £200 for a one time service",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Service Fee",
        introTitle: "Charge for a one-off payment",
        introDescription: "Request a payment from someone who owes you",
        formValues: {},
      },
    },
  ],
  [SubSegment.Accountancy]: [
    {
      key: "accountancy_plan_a",
      useCase: "Collect recurring fees for ongoing services",
      title: "Recurring fees for ongoing services",
      description: "e.g. £200 per month for accounting services retainer",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Monthly Accountancy Fees",
        introTitle: "Collect recurring fees",
        introDescription:
          "Use the subscription option to set up customers on plans for ongoing services or for a set number of payments.",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "accountancy_plan_b",
      useCase: "Offer subscription plans",
      title: "Subscription plan",
      description: "e.g. £20 per month for accounting software license",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Gold Accounting Plan",
        introTitle: "Offer subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "accountancy_mandate",
      useCase:
        "Auto charge regular clients based on usage, services or time-worked",
      title: "Auto charge regular clients",
      description:
        "Collect variable payments based on services delivered or time-worked",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    },
    {
      key: "accountancy_oneoff",
      useCase: "Charge for one-off services",
      title: "One-off service or product",
      description: "e.g. an instant payment of £500 for filing a tax return",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Payment for Invoice 1500",
        introTitle: "Charge for a one-off payment",
        introDescription:
          "Share a payment link or include it in invoices to receive payments",
        formValues: {},
      },
    },
  ],
  [SubSegment.Healthcare]: [
    {
      key: "healthcare_plan",
      useCase: "Offer subscription or membership plans",
      title: "Subscription or membership plan",
      description: "e.g. £200 per month for a healthcare subscription",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Gold Membership",
        introTitle: "Offer subscription or membership plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "healthcare_instalment",
      useCase: "Split a large payment into instalments",
      title: "Split a large payment into instalments",
      description: "e.g. £600 treatment split into 6 monthly payments of £100",
      templateProps: {
        paymentType: PaymentType.Instalment,
        paymentNamePlaceholder: "e.g. Treatment Plan",
        introTitle: "Split a large payment into instalments",
        introDescription:
          "Set up the details below, then invite customers to the plan",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          numberOfInstalments: 3,
        },
      },
    },
    {
      key: "healthcare_oneoff",
      useCase: "Charge for one-off service or product",
      title: "One-off service or product",
      description: "e.g. an instant payment of £500 for a a treatment",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Dental Treatment",
        introTitle: "Charge for a one-off payment",
        introDescription:
          "Share a payment link or include it in invoices to receive payments",
        formValues: {},
      },
    },
    {
      key: "healthcare_mandate",
      useCase: "Auto charge regular customers based on usage or services",
      title: "Auto charge regular customers based on usage",
      description:
        "Collect variable payments based on time-worked or services delivered",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    },
  ],
  [SubSegment.Gym]: [
    {
      key: "gym_plan_a",
      useCase: "Offer monthly membership plans",
      title: "Monthly membership plans",
      description: "e.g. £30 per month for a family membership plan",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Family Membership Plan",
        introTitle: "Offer membership plans",
        introDescription:
          "Use the subscription option to create membership plans",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "gym_plan_b",
      useCase: "Offer coaching or subscription plans",
      title: "Coaching or subscription plans",
      description: "e.g. £30 a month for an online coaching subscription",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Online Coaching Plan",
        introTitle: "Offer coaching or subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "gym_plan_count_6",
      useCase: "Offer fixed-term payment plans e.g., a block of sessions",
      title: "Fixed-term plan e.g. block of sessions",
      description: "e.g. 6 monthly payments of £250 for a training plan",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Personal Training Plan",
        introTitle: "Offer fixed-term payment plans ",
        introDescription:
          "Use the subscription option to collect recurring fees for a set number of payments",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          timeframe: Timeframe.NumberOfPayment,
          count: 3,
        },
      },
    },
    {
      key: "gym_oneoff",
      useCase: "Charge for one-off fees or products",
      title: "One-off payment",
      description:
        "e.g. collect an instant payment of £200 for a fitness bootcamp",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Coaching Package",
        introTitle: "Charge for a one-off payment",
        introDescription:
          "Share a payment link or include it on your website to receive payments",
        formValues: {},
      },
    },
  ],
  [SubSegment.Education]: [
    {
      key: "education_plan",
      useCase: "Offer ongoing membership or subscription plans",
      title: "Ongoing membership or subscription plan",
      description: "e.g. £30 a month until further notice",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Class Subscription",
        introTitle: "Offer membership or subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "education_plan_count_3",
      useCase: "Offer fixed-term payment plans, e.g., a term of classes",
      title: "Fixed term payment plan",
      description: "e.g. 3 monthly payments of £200 for a term of classes",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Course Payments",
        introTitle: "Offer fixed-term payment plans",
        introDescription:
          "Use the subscription option to collect recurring fees for a set number of payments",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          timeframe: Timeframe.NumberOfPayment,
          count: 3,
        },
      },
    },
    {
      key: "education_mandate",
      useCase:
        "Auto charge regular clients based on sessions attended or usage",
      title: "Auto charge regular clients based on usage",
      description: "Collect variable amounts based on use or sessions attended",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    },
    {
      key: "education_oneoff",
      useCase: "Charge for one-off fees",
      title: "One-off fee",
      description: "e.g. £500 for a course",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Registration Fees",
        introTitle: "Charge a one-off fee",
        introDescription:
          "Share a payment link or include it on your website to receive payments",
        formValues: {},
      },
    },
  ],
  [SubSegment.PT]: [
    {
      key: "pt_plan",
      useCase: "Offer ongoing membership or subscription plans",
      title: "Membership or subscription plan",
      description:
        "e.g. £300 a month until further notice for coaching services",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. Personal Training Plan",
        introTitle: "Offer membership or subscription plans",
        introDescription:
          "Set up the details below, then invite customers to sign up for it",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
        },
      },
    },
    {
      key: "pt_plan_count_3",
      useCase: "Offer fixed-term payment plans e.g., a block of sessions",
      title: "Fixed-term plan e.g. block of sessions",
      description: "e.g. 3 monthly payments of £250 for a training plan",
      templateProps: {
        paymentType: PaymentType.Plan,
        paymentNamePlaceholder: "e.g. 3 Month Training Plan, Paid Monthly",
        introTitle: "Offer fixed-term payment plans",
        introDescription:
          "Use the subscription option to collect recurring fees for a set number of payments",
        formValues: {
          interval_unit: IntervalUnit.Monthly,
          timeframe: Timeframe.NumberOfPayment,
          count: 3,
        },
      },
    },
    {
      key: "pt_oneoff",
      useCase: "Charge for one-off payments",
      title: "One-off payment",
      description:
        "e.g. collect an instant payment of £200 for a coaching package",
      templateProps: {
        paymentType: PaymentType.OneOff,
        paymentNamePlaceholder: "e.g. Coaching Package",
        introTitle: "Charge for a one-off payment",
        introDescription:
          "Share a payment link or include it on your website to receive payments",
        formValues: {},
      },
    },
    {
      key: "pt_mandate",
      useCase: "Auto charge regular clients for sessions as needed",
      title: "Auto charge regular clients as needed",
      description: "Collect ad-hoc payments for sessions whenever due",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    },
  ],
} as const;

export function getTemplatesForSubSegment(
  subSegment: SubSegment,
  opts: { withAddCustomer?: boolean; withTestPayment?: boolean } = {}
) {
  const { withAddCustomer = false, withTestPayment = false } = opts;

  const result = [];

  if (withAddCustomer) {
    result.push({
      key: "add_customer",
      useCase: "Set up a Direct Debit agreement, schedule payments later",
      title: "Add customer",
      description: "Set up a Direct Debit agreement, schedule payments later",
      templateProps: {
        paymentType: PaymentType.Mandate,
        formValues: {},
      },
    });
  }

  if (withTestPayment) {
    result.push({
      key: "test_payment",
      useCase: "Take a £1 test payment",
      title: "Take a £1 test payment",
      description: "Take a payment from yourself to test out the experience",
      templateProps: {
        paymentType: PaymentType.OneOff,
        formValues: { amount: 1, currency: Currency.Gbp, name: "Test Payment" },
      },
    });
  }

  const templatesForSubSegment = BASE_TEMPLATES[subSegment] ?? [];
  result.push(...templatesForSubSegment);

  return result;
}
